import React from 'react';
import { BRAND_CHEVRON_PATHS } from './BrandChevron';
import {
  Bell, MagnifyingGlass, Gear, Eye, EyeSlash,
  PencilSimple, Trash, UploadSimple,
  Link, Copy, ArrowClockwise, Funnel, TrendUp,
  Star, Check, Plus, X, CaretLeft,
  DeviceMobile, Monitor, Sun, Moon,
  Shield, ShieldCheck,
  Globe, ChatCircle, CurrencyEur, Coin, CreditCard,
  DotsThreeVertical, Warning,
  ArrowUpRight, ArrowDownLeft,
  SignOut, Wallet, Headset,
  ChartLine, User, ArrowRight,
  Export,
} from '@phosphor-icons/react';

// ─────────────────────────────────────────────────────────────────────────
// Icon-System v2 — einheitliches App-Icon-Set.
//
// Dual-Mode: Icons können entweder Phosphor-Komponenten (Funktion) sein
// oder custom SVG-Pfade (JSX). Brand-Icons (home 2×2 Grid, walletNav,
// reports, chevrons, Logos) bleiben als eigene Pfade. Utility-Icons
// (bell, eye, gear, trash …) kommen aus Phosphor Light.
//
// Die Navigations-Icons leben separat in NavGlyph.js.
// ─────────────────────────────────────────────────────────────────────────

// weight='light' = Standard (iOS-nah). Per Prop überschreibbar.
export const Icon = ({ d, size = 22, stroke = 'currentColor', sw = 2.4, fill = 'none', style, weight = 'light' }) => {
  // Phosphor-Komponenten sind forwardRef-Objekte — typeof ergibt 'object', nicht 'function'.
  // React.isValidElement() ist false für Komponenten, true für fertige JSX-Elemente (brand SVGs).
  if (d && !React.isValidElement(d)) {
    const Ph = d;
    return <Ph size={size} color={stroke} weight={weight} style={{ display: 'block', flexShrink: 0, ...style }} />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', flexShrink: 0, ...style }}
    >
      {d}
    </svg>
  );
};

export const Icons = {
  // ── Brand-Icons: custom SVG, bleiben unverändert ───────────────────────

  // home: 2×2 grid, top-left gefüllt (echoes red. block)
  home: (
    <>
      <rect x="3" y="3" width="7.6" height="7.6" rx="2" fill="currentColor" stroke="none" />
      <rect x="13.4" y="3" width="7.6" height="7.6" rx="2" />
      <rect x="3" y="13.4" width="7.6" height="7.6" rx="2" />
      <rect x="13.4" y="13.4" width="7.6" height="7.6" rx="2" />
    </>
  ),
  // walletNav = „Transaktionen": gegenläufige Pfeile, gefüllte obere Spitze als Akzent
  walletNav: (
    <>
      <polygon points="7.5 3 4 7.2 11 7.2" fill="currentColor" stroke="none" />
      <path d="M7.5 7v13" />
      <path d="M16.5 4v13" />
      <polyline points="13 16.8 16.5 21 20 16.8" />
    </>
  ),
  // Legacy wallet — reine Listen-Zeilen
  wallet: (
    <>
      <path d="M3 6h18" />
      <path d="M3 12h14" />
      <path d="M3 18h10" />
    </>
  ),
  // reports = „Berichte" — Dokument/Blatt mit gefülltem Eselsohr als Akzent
  reports: (
    <>
      <path d="M13.5 3.5H7.5A1.6 1.6 0 0 0 5.9 5.1V18.9A1.6 1.6 0 0 0 7.5 20.5H16.5A1.6 1.6 0 0 0 18.1 18.9V8.1L13.5 3.5Z" />
      <polygon points="13.5 3.5 18.1 8.1 13.5 8.1" fill="currentColor" stroke="none" />
      <path d="M9 12.4h6M9 15.4h6M9 18.4h3.6" />
    </>
  ),
  // candles — Kerzen; erster Body gefüllt als Akzent
  candles: (
    <>
      <line x1="7" y1="3.5" x2="7" y2="20.5" />
      <rect x="4.4" y="7.5" width="5.2" height="7" rx="1.4" fill="currentColor" stroke="none" />
      <line x1="17" y1="3.5" x2="17" y2="20.5" />
      <rect x="14.4" y="10.5" width="5.2" height="6" rx="1.4" />
    </>
  ),
  // Marken-Pfeil — Geometrie zentral in BrandChevron.js
  chevR: <path d={BRAND_CHEVRON_PATHS.right} />,
  chevL: <path d={BRAND_CHEVRON_PATHS.left} />,
  chevD: <path d={BRAND_CHEVRON_PATHS.down} />,
  chevU: <path d={BRAND_CHEVRON_PATHS.up} />,
  // swap — gegenläufige Pfeile, gefüllte obere Spitze als Akzent
  swap: (
    <>
      <polygon points="7.5 3 4 7.2 11 7.2" fill="currentColor" stroke="none" />
      <path d="M7.5 7v13" />
      <path d="M16.5 4v13" />
      <polyline points="13 16.8 16.5 21 20 16.8" />
    </>
  ),
  // bolt — vollflächig gefüllter Blitz
  bolt: <path d="M13 3 5 13h5l-1 8 9-12h-5z" fill="currentColor" stroke="none" />,
  // Logos (bleiben immer custom)
  google: (
    <path d="M21.35 11.1H12v3.2h5.35a4.62 4.62 0 0 1-4.5 3.5 5 5 0 0 1-5-5 5 5 0 0 1 5-5 4.6 4.6 0 0 1 3.2 1.25l2.25-2.25A8 8 0 0 0 12 4a8 8 0 1 0 8 8 7.4 7.4 0 0 0-.65-3.1z" fill="currentColor" stroke="none" />
  ),
  apple: (
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.37.07 2.32.75 3.13.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.58 3.99zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor" stroke="none" />
  ),
  socialX: (
    <path d="M4 4l6.5 8.5L4 20h2.2l5.1-6.1L16 20h4l-6.8-8.8L20 4h-2.2l-4.9 5.8L8 4H4z" fill="currentColor" stroke="none" />
  ),
  socialInstagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="16.8" cy="7.2" r="1.2" />
    </>
  ),
  socialLinkedin: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" />
      <circle cx="7.8" cy="7.8" r="1.1" fill="currentColor" stroke="none" />
      <path d="M7.8 11v6" />
      <path d="M12 17v-4.5c0-1.5 1.1-2.5 2.5-2.5s2.5 1 2.5 2.5V17" />
      <path d="M12 11v6" />
    </>
  ),
  socialDiscord: (
    <path d="M18.6 5.3A17 17 0 0 0 15 4.2a.07.07 0 0 0-.07.03c-.3.5-.6 1.1-.82 1.6a15.7 15.7 0 0 0-4.22 0 12.7 12.7 0 0 0-.83-1.6A.07.07 0 0 0 9 4.2a17 17 0 0 0-3.6 1.1A.07.07 0 0 0 5.36 5.4C3.02 9 2.4 12.4 2.7 15.8c0 .02.01.04.03.05a17.1 17.1 0 0 0 5.1 2.55.07.07 0 0 0 .08-.03c.39-.53.74-1.1 1.04-1.69a.07.07 0 0 0-.04-.1 11.3 11.3 0 0 1-1.6-.76.07.07 0 0 1-.01-.12l.32-.25a.07.07 0 0 1 .07-.01c3.37 1.52 7 1.52 10.34 0a.07.07 0 0 1 .07.01l.32.25c.04.03.04.1-.01.12-.52.3-1.06.55-1.6.76a.07.07 0 0 0-.04.1c.31.6.66 1.16 1.04 1.69a.07.07 0 0 0 .08.03 17.1 17.1 0 0 0 5.11-2.55.07.07 0 0 0 .03-.05c.37-3.79-.62-7.08-2.63-10.4a.06.06 0 0 0-.04-.03zM8.52 13.8c-.9 0-1.65-.83-1.65-1.85s.73-1.85 1.65-1.85c.93 0 1.67.84 1.65 1.85 0 1.02-.73 1.85-1.65 1.85zm6.1 0c-.9 0-1.65-.83-1.65-1.85s.73-1.85 1.65-1.85c.93 0 1.67.84 1.65 1.85 0 1.02-.72 1.85-1.65 1.85z" fill="currentColor" stroke="none" />
  ),
  // privacyShield — Shield mit Schlüsselloch (Fill-Akzent = Kreis + schlanker
  // Schlitz). Ersetzt das frühere klobige Vorhängeschloss. Icon-System v2.
  privacyShield: (
    <>
      <path d="M12 3l7 2.4v5.3c0 4.4-3 7.7-7 8.9-4-1.2-7-4.5-7-8.9V5.4L12 3z" />
      <circle cx="12" cy="10.8" r="1.7" fill="currentColor" stroke="none" />
      <path d="M12 12.2 L10.9 15.4 H13.1 Z" fill="currentColor" stroke="none" />
    </>
  ),
  // 2FA — Schlüssel
  shield2FA: (
    <>
      <circle cx="8" cy="15.5" r="3.2" />
      <path d="M10.3 13.2l8.2-8.2" />
      <path d="M15.5 8l2.3 2.3" />
      <path d="M18 5.5l2.3 2.3" />
    </>
  ),
  // storefront — für „Märkte" (zu spezifisch)
  storefront: (
    <>
      <path d="M3.5 9 L5 4.5 H19 L20.5 9" />
      <path d="M3.5 9c1.7 2.3 3.8 2.3 5.5 0 1.7 2.3 3.8 2.3 5.5 0 1.7 2.3 3.8 2.3 5.5 0" />
      <path d="M5.3 10.7 V20 H18.7 V10.7" />
      <path d="M10.8 20 V14 H15 V20" />
    </>
  ),

  // ── Utility-Icons: Phosphor Light ─────────────────────────────────────

  bell:        Bell,
  search:      MagnifyingGlass,
  cog:         Gear,
  eye:         Eye,
  eyeOff:      EyeSlash,
  edit:        PencilSimple,
  trash:       Trash,
  trashSettings: Trash,
  export:      Export,
  upload:      UploadSimple,
  link:        Link,
  copy:        Copy,
  refresh:     ArrowClockwise,
  filter:      Funnel,
  trend:       TrendUp,
  star:        Star,
  starFilled:  Star,   // weight='fill' wird via <Icon weight="fill"> gesetzt
  check:       Check,
  plus:        Plus,
  close:       X,
  accountDelete: X,
  back:        CaretLeft,
  phone:       DeviceMobile,
  desktop:     Monitor,
  sun:         Sun,
  moon:        Moon,
  themeMoon:   Moon,
  shieldCheck: ShieldCheck,
  shield:      Shield,
  globe:       Globe,
  language:    ChatCircle,
  currency:    CurrencyEur,
  coin:        Coin,
  card:        CreditCard,
  kebab:       DotsThreeVertical,
  alert:       Warning,
  arrowDR:     ArrowUpRight,
  arrowDL:     ArrowDownLeft,
  logoutDoor:  SignOut,
  walletCard:  Wallet,
  headset:     Headset,
  markets:     ChartLine,
  profile:     User,
  arrowRight:  ArrowRight,
};
